import type { Word, WordSummary } from '@/schemas/word';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WORDS_PAGE_SIZE, wordsApi } from './api';
import { excludeIds, mergeRelevanceResults, rankContainsResults } from './search-relevance';
import {
  DEFAULT_WORD_SORT,
  EMPTY_FILTERS,
  type NoyauFilterValue,
  type WordFilters,
  type WordSort,
  activeFilterCount,
  hasActiveFilters,
  presetToSince,
} from './words-query';

type NavEntry = { wordId: string; query: string };
type NavState = { entries: NavEntry[]; index: number };

/** Minimum query length before a text search fires (filters fire standalone). */
const MIN_QUERY_LENGTH = 1;
/** Debounce applied to the search input before it drives a query. */
const SEARCH_DEBOUNCE_MS = 300;
/** Hard ceiling on accumulated results in the legacy (non-relevance) path; past it the UI asks to refine. */
const RESULTS_CAP = 2000;
/**
 * Hard ceiling on the contains-match set fetched for relevance ranking. Beyond
 * this, only the first page (`WORDS_PAGE_SIZE`) gets ranked by match quality;
 * the rest is dropped and the UI asks the user to refine instead.
 */
const CONTAINS_CAP = 500;

type WordsNavContext = {
  query: string;
  setQuery: (q: string) => void;
  results: WordSummary[];
  isLoading: boolean;
  /** True when more pages are available for the current query/filters. */
  hasNextPage: boolean;
  /** Fetch the next page of results (infinite scroll). */
  fetchNextPage: () => void;
  /** True while a subsequent page is loading. */
  isFetchingNextPage: boolean;
  /** True when results were truncated (see {@link RESULTS_CAP}/{@link CONTAINS_CAP}). */
  capped: boolean;
  canBack: boolean;
  canForward: boolean;
  pushWord: (wordId: string, options?: { silent?: boolean }) => void;
  goBack: () => void;
  goForward: () => void;
  /** Navigate back to the search list (used by the mobile detail back arrow). */
  goToList: () => void;
  // Filters + sort (sticky across word navigation; not part of the history stack).
  filters: WordFilters;
  sort: WordSort;
  toggleNoyau: (value: NoyauFilterValue) => void;
  setTagFilter: (tags: string[]) => void;
  setRevisedPreset: (preset: WordFilters['revisedPreset']) => void;
  setToCheckOnly: (toCheckOnly: boolean) => void;
  setSort: (sort: WordSort) => void;
  resetFilters: () => void;
  activeFilterCount: number;
};

const WordsNavCtx = createContext<WordsNavContext | null>(null);

export function WordsNavProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [query, setQueryState] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [nav, setNav] = useState<NavState>({ entries: [], index: -1 });
  const [filters, setFilters] = useState<WordFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<WordSort>(DEFAULT_WORD_SORT);
  const [revealCount, setRevealCount] = useState(WORDS_PAGE_SIZE);

  // Stable refs so callbacks don't need these as deps
  const navRef = useRef(nav);
  navRef.current = nav;
  const queryRef = useRef(query);
  queryRef.current = query;

  // Debounce the input before it drives the query (avoids a request per keystroke).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  const hasQuery = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;
  // "Pertinence" only makes sense against an active query; a filters-only or
  // empty search falls back to plain alphabetical order (see `effectiveSort`).
  const relevanceMode = sort === 'relevance' && hasQuery;
  const effectiveSort: WordSort = sort === 'relevance' ? 'title-asc' : sort;
  const legacyEnabled = !relevanceMode && (hasQuery || hasActiveFilters(filters));

  const filterParams = {
    noyaux: filters.noyaux,
    tags: filters.tags,
    revisedSince: filters.revisedPreset
      ? presetToSince(filters.revisedPreset, new Date())
      : undefined,
    toCheckOnly: filters.toCheckOnly,
  };

  // ── Legacy path: today's server-sorted, server-paginated search ─────────────
  const {
    data: legacyData,
    isLoading: legacyIsLoading,
    hasNextPage: legacyHasNextPage = false,
    fetchNextPage: legacyFetchNextPage,
    isFetchingNextPage: legacyIsFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['words', 'search', debouncedQuery, filters, effectiveSort],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      wordsApi.search(debouncedQuery, { ...filterParams, sort: effectiveSort }, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 60_000,
    enabled: legacyEnabled,
  });

  // ── Relevance path: exact-match + ranked contains-match, fetched in parallel ─
  const exactQuery = useQuery({
    queryKey: ['words', 'search-exact', debouncedQuery, filters],
    queryFn: () =>
      wordsApi.search(debouncedQuery, { ...filterParams, exact: true, sort: 'title-asc' }, 1),
    staleTime: 60_000,
    enabled: relevanceMode,
  });

  const containsQuery = useQuery({
    queryKey: ['words', 'search-contains', debouncedQuery, filters],
    queryFn: () => wordsApi.searchUpTo(debouncedQuery, filterParams, CONTAINS_CAP),
    staleTime: 60_000,
    enabled: relevanceMode,
  });

  // Reset how much of the ranked relevance list is revealed whenever the query
  // or filters change — otherwise a narrower follow-up query would inherit a
  // stale reveal count from a previous, longer result set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedQuery and filters are change triggers, not values used inside the effect
  useEffect(() => {
    setRevealCount(WORDS_PAGE_SIZE);
  }, [debouncedQuery, filters]);

  // Prime the per-word detail cache from every fetched source. meta/list already
  // returns `content`, so a clicked result renders from cache without a second
  // fetch — essential on live devdb, where single-fetch-by-slug isn't supported.
  useEffect(() => {
    const sources: { words: Word[] }[] = [...(legacyData?.pages ?? [])];
    if (exactQuery.data) sources.push(exactQuery.data);
    if (containsQuery.data) sources.push(containsQuery.data);
    for (const source of sources) {
      for (const word of source.words) {
        // Keyed by guid (Word.id) — the value carried in the /words/$wordId route.
        queryClient.setQueryData(['word', word.id], word);
      }
    }
  }, [legacyData, exactQuery.data, containsQuery.data, queryClient]);

  // Flatten legacy pages, dedupe by id (offset pages can overlap on mutation), cap.
  const flatLegacy: WordSummary[] = [];
  const seenLegacy = new Set<string>();
  for (const page of legacyData?.pages ?? []) {
    for (const item of page.items) {
      if (seenLegacy.has(item.id)) continue;
      seenLegacy.add(item.id);
      flatLegacy.push(item);
    }
  }
  const legacyCapped = flatLegacy.length > RESULTS_CAP;
  const legacyResults = legacyCapped ? flatLegacy.slice(0, RESULTS_CAP) : flatLegacy;

  // Combine the exact tier with the ranked contains tier. When the contains
  // fetch was truncated (more than CONTAINS_CAP total matches), only the first
  // server page gets ranked by match quality; rows beyond that are appended in
  // plain (server/title) order, unranked — still shown and scrollable, just
  // not quality-ranked. The "Affinez…" message (see `capped`) tells the user
  // there may be more matches than the CONTAINS_CAP fetched altogether.
  const exactItems = exactQuery.data?.items ?? [];
  const exactIds = new Set(exactItems.map((item) => item.id));
  const containsTruncated = containsQuery.data?.truncated ?? false;
  const containsFiltered = excludeIds(containsQuery.data?.items ?? [], exactIds);
  const rankable = containsTruncated
    ? containsFiltered.slice(0, WORDS_PAGE_SIZE)
    : containsFiltered;
  const overflow = containsTruncated ? containsFiltered.slice(WORDS_PAGE_SIZE) : [];
  const containsRanked = [...rankContainsResults(rankable, debouncedQuery), ...overflow];
  const relevanceResults = mergeRelevanceResults({ exact: exactItems, containsRanked });
  const relevanceCapped = containsTruncated;

  const results = relevanceMode ? relevanceResults.slice(0, revealCount) : legacyResults;
  const isLoading = relevanceMode
    ? exactQuery.isLoading || containsQuery.isLoading
    : legacyIsLoading;
  const hasNextPage = relevanceMode ? revealCount < relevanceResults.length : legacyHasNextPage;
  const isFetchingNextPage = relevanceMode ? false : legacyIsFetchingNextPage;
  const capped = relevanceMode ? relevanceCapped : legacyCapped;

  const setQuery = useCallback((q: string) => setQueryState(q), []);

  // Toggle a noyau value in/out of the filter (OR within the dimension).
  const toggleNoyau = useCallback((value: NoyauFilterValue) => {
    setFilters((prev) => ({
      ...prev,
      noyaux: prev.noyaux.includes(value)
        ? prev.noyaux.filter((v) => v !== value)
        : [...prev.noyaux, value],
    }));
  }, []);

  const setTagFilter = useCallback((tags: string[]) => {
    setFilters((prev) => ({ ...prev, tags }));
  }, []);

  const setRevisedPreset = useCallback((revisedPreset: WordFilters['revisedPreset']) => {
    setFilters((prev) => ({ ...prev, revisedPreset }));
  }, []);

  const setToCheckOnly = useCallback((toCheckOnly: boolean) => {
    setFilters((prev) => ({ ...prev, toCheckOnly }));
  }, []);

  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  // Pushes a visited word into the history stack and optionally navigates to it.
  // Pass { silent: true } when the navigation is already handled elsewhere (e.g. a Link click).
  const pushWord = useCallback(
    (wordId: string, options?: { silent?: boolean }) => {
      const q = queryRef.current;
      setNav((prev) => {
        const entries = [...prev.entries.slice(0, prev.index + 1), { wordId, query: q }];
        return { entries, index: entries.length - 1 };
      });
      if (!options?.silent) {
        void navigate({ to: '/words/$wordId', params: { wordId } });
      }
    },
    [navigate],
  );

  const goBack = useCallback(() => {
    const current = navRef.current;
    const newIndex = current.index - 1;
    if (newIndex < 0) return;
    const entry = current.entries[newIndex];
    if (!entry) return;
    setNav((prev) => ({ ...prev, index: newIndex }));
    setQueryState(entry.query);
    void navigate({ to: '/words/$wordId', params: { wordId: entry.wordId } });
  }, [navigate]);

  const goForward = useCallback(() => {
    const current = navRef.current;
    const newIndex = current.index + 1;
    if (newIndex >= current.entries.length) return;
    const entry = current.entries[newIndex];
    if (!entry) return;
    setNav((prev) => ({ ...prev, index: newIndex }));
    setQueryState(entry.query);
    void navigate({ to: '/words/$wordId', params: { wordId: entry.wordId } });
  }, [navigate]);

  const goToList = useCallback(() => {
    void navigate({ to: '/words' });
  }, [navigate]);

  return (
    <WordsNavCtx.Provider
      value={{
        query,
        setQuery,
        results,
        isLoading,
        hasNextPage,
        fetchNextPage: () => {
          if (relevanceMode) {
            setRevealCount((n) => Math.min(n + WORDS_PAGE_SIZE, relevanceResults.length));
          } else {
            void legacyFetchNextPage();
          }
        },
        isFetchingNextPage,
        capped,
        canBack: nav.index > 0,
        canForward: nav.index < nav.entries.length - 1,
        pushWord,
        goBack,
        goForward,
        goToList,
        filters,
        sort,
        toggleNoyau,
        setTagFilter,
        setRevisedPreset,
        setToCheckOnly,
        setSort,
        resetFilters,
        activeFilterCount: activeFilterCount(filters),
      }}
    >
      {children}
    </WordsNavCtx.Provider>
  );
}

export function useWordsNav() {
  const ctx = useContext(WordsNavCtx);
  if (!ctx) throw new Error('useWordsNav must be inside WordsNavProvider');
  return ctx;
}
